import { useEffect, useMemo, useRef } from 'react';

import { usePathname } from 'expo-router';
import { useStoreContext } from 'features/store';
import { usePosDataQuery } from 'features/store/hooks/use-pos-data-query';

import { ILegacyCartEntry } from '@rbi-ctg/menu';
import { useCart } from 'hooks/use-cart';
import { useEnableUnifiedMenu } from 'hooks/use-enable-unified-menu';
import { routes } from 'navigation/routing';
import { useLoyaltyContext } from 'state/loyalty';
import { useLoyaltyOfferAvailabilityEvaluation } from 'state/loyalty/offers/hooks/use-loyalty-offer-availability-evaluation';
import { LoyaltyAppliedOffer } from 'state/loyalty/types';
import { useMainMenuContext } from 'state/menu/main-menu';
import { useServiceModeContext } from 'state/service-mode';
import { ServiceMode } from 'state/service-mode/types';
import { useUnifiedMenuContext } from 'state/unified-menu/unified-menu-context';
import { useMenuQueryOptions } from 'state/unified-menu/use-menu-query-options';
import { getUnavailableCartEntries, isCartEntryAvailableOneMenu } from 'utils/availability';
import logger from 'utils/logger';

interface UseCartAvailabilityCheckParams {
  cartEntries: ILegacyCartEntry[];
  isStoreChangeHydrationInProgress: boolean;
  appliedOffers: LoyaltyAppliedOffer[];
  loyaltyUserId?: string | null;
}

/**
 * React hook that validates cart entry availability when the user changes stores.
 *
 * This checks for cart entries availability for a new selected store and service mode
 * during hydration/rehydration flows. It supports both One Menu and Legacy store types, applying
 * different availability checks accordingly:
 *
 * - **One Menu**:
 *   - Waits until One Menu data (main menu, indexed menu) is fully loaded.
 *   - Availabity check to filter out unavailable entries based on
 *     the indexed menu, service mode, and active day parts.
 *
 * - **Legacy**:
 *   - Fetches POS pricing data for the store using `refetchPosData`.
 *   - If no POS data is available, all entries are considered unavailable.
 *
 * When unavailable entries are found:
 * - Sets confirmation parameters with `setOnConfirmStoreChangeParams`.
 * - Provides a callback to remove the unavailable entries from the cart.
 * - Triggers the "not available" modal to inform the user.
 *
 * @param {UseCartAvailabilityCheckParams} params - Hook parameters.
 * @param {ILegacyCartEntry[]} params.cartEntries - The list of cart entries to check for availability.
 * @param {boolean} params.isStoreChangeHydrationInProgress - Indicates if store-change hydration is in progress.
 *
 * @returns {void}
 */

export const useCartAvailabilityCheck = ({
  cartEntries,
  isStoreChangeHydrationInProgress,
  appliedOffers,
  loyaltyUserId,
}: UseCartAvailabilityCheckParams) => {
  const { removeAllFromCart } = useCart();
  const { store, setOnConfirmStoreChangeParams } = useStoreContext();
  const { posServiceMode } = useMenuQueryOptions();
  const { isLoading: isOneMenuLoading, mainMenu, indexedMenu } = useUnifiedMenuContext();
  const { activeDayParts } = useMainMenuContext();
  const { serviceMode } = useServiceModeContext();
  const { isOfferUnavailable, findCmsOfferByCmsId } = useLoyaltyOfferAvailabilityEvaluation();
  const { evaluateLoyaltyUserOffers } = useLoyaltyContext();

  // TODO: Replace with useEffectEvent when we upgrade to React 19.2
  // https://react.dev/reference/react/useEffectEvent
  const evaluateOffers = useRef(evaluateLoyaltyUserOffers);
  evaluateOffers.current = evaluateLoyaltyUserOffers;

  const isUnifiedMenuEnabled = useEnableUnifiedMenu();
  const pathname = usePathname();
  const isFromMenuScreen = pathname.startsWith(routes.menu);

  const { refetch: refetchPosData } = usePosDataQuery({
    lazy: true,
    storeNumber: '',
  });

  const dayPartIds = useMemo(() => activeDayParts.map(dp => dp.key), [activeDayParts]);

  const isUnifiedMenuLoaded = indexedMenu?.isLoaded() ?? false;

  useEffect(() => {
    if (
      isStoreChangeHydrationInProgress ||
      !cartEntries.length ||
      !appliedOffers.length ||
      // We are already checking this in the cart with useLoyaltyCartEntriesEvaluation. We should do this check only for the menu screen.
      !isFromMenuScreen ||
      // Wait for unified menu to be loaded before evaluating offers
      !isUnifiedMenuLoaded
    ) {
      return;
    }
    evaluateOffers.current(loyaltyUserId, appliedOffers, cartEntries);
  }, [
    cartEntries,
    isStoreChangeHydrationInProgress,
    loyaltyUserId,
    appliedOffers,
    isFromMenuScreen,
    pathname,
    isUnifiedMenuLoaded,
  ]);

  // @TODO: Investigate how and when to trigger this as a manual function instead of an useEffect
  // The main issue with not running this as a side effect is that the price order mutation will be
  // triggered with the unavailable cartEntries.
  // We will need to wait to call the price order mutation after this check is done.
  useEffect(() => {
    // Do nothing if there are no cart entries to check,
    // store change hydration is running or the feature flag is disabled
    if ((!cartEntries.length && !appliedOffers.length) || isStoreChangeHydrationInProgress) {
      return;
    }

    const isDeliveryMode = serviceMode === ServiceMode.DELIVERY;
    const isOneMenuReady = !isOneMenuLoading && !!mainMenu.length && !!indexedMenu;

    const removeUnavailableCartEntries = (unavailableCartEntries: ILegacyCartEntry[]) => {
      if (!unavailableCartEntries.length) {
        return;
      }

      // Display the not available modal and remove the unavailable entries from the cart.
      setOnConfirmStoreChangeParams({
        callback: () => removeAllFromCart(unavailableCartEntries),
        newStore: store,
        unavailableCartEntries,
      });
    };

    // One Menu check
    if (isUnifiedMenuEnabled) {
      // Ensure we have all the necessary One Menu data to check for entries availability.
      if (!isOneMenuReady) {
        return;
      }

      const unavailableCartEntries = cartEntries.filter(
        entry => !isCartEntryAvailableOneMenu(entry, indexedMenu, posServiceMode, dayPartIds)
      );

      appliedOffers.forEach(appliedOffer => {
        const cmsOffer = findCmsOfferByCmsId(appliedOffer.cmsId);
        if (!cmsOffer) {
          return;
        }

        if (isOfferUnavailable(cmsOffer)) {
          const unavailableEntry = cartEntries.find(entry => entry.cartId === appliedOffer.cartId);

          if (unavailableEntry) {
            unavailableCartEntries.push(unavailableEntry);
          }
        }
      });

      removeUnavailableCartEntries(unavailableCartEntries);
      return;
    }

    // Legacy check
    const getUnavailableCartEntriesLegacy = async (): Promise<ILegacyCartEntry[]> => {
      const selectedStorePrices = await refetchPosData({
        storeNumber: store.number || null,
        isDelivery: isDeliveryMode,
      });

      if (!selectedStorePrices) {
        logger.warn({
          message: 'PLU data for store is null. Flagging all cart entries as unavailable.',
          storeNumber: store.number,
          storeId: store._id,
          posVendor: store.posVendor,
        });

        // If we don't have posData information, then we mark all the cart entries as not available.
        return cartEntries;
      }

      const unavailableCartEntries = getUnavailableCartEntries(
        cartEntries,
        store,
        selectedStorePrices.posData,
        serviceMode as ServiceMode
      );

      return unavailableCartEntries;
    };

    const runLegacyAvailabilityCheck = async () => {
      const unavailableCartEntries = await getUnavailableCartEntriesLegacy();
      removeUnavailableCartEntries(unavailableCartEntries);
    };

    runLegacyAvailabilityCheck();
  }, [
    appliedOffers,
    isOfferUnavailable,
    findCmsOfferByCmsId,
    isOneMenuLoading,
    mainMenu.length,
    indexedMenu,
    posServiceMode,
    dayPartIds,
    refetchPosData,
    serviceMode,
    store,
    setOnConfirmStoreChangeParams,
    removeAllFromCart,
    isStoreChangeHydrationInProgress,
    isUnifiedMenuEnabled,
    cartEntries,
  ]);
};
